/**
 * Quick in-browser dark preview, used only when the server has no preview renderer set up
 * (email_preview_render returns no images; CONTRACTS §7a). It approximates the two ways mail apps darken an email:
 *  - Apple Mail (and others that honor it): the email's own @media (prefers-color-scheme: dark) rules.
 *  - Gmail / Outlook apps: automatic color inversion when the email has no dark styles; Outlook.com's
 *    [data-ogsc] / [data-ogsb] selectors are switched on.
 * No scripts: the result runs in a sandboxed frame.
 */
export interface DarkResult {
  html: string;
  rule: "own_dark_styles" | "inverted";
  explanation: string;
}

const DARK_MEDIA = /@media\s+(?:only\s+)?(?:screen\s+and\s+)?\(\s*prefers-color-scheme\s*:\s*dark\s*\)/gi;

export function hasOwnDarkStyles(html: string): boolean {
  DARK_MEDIA.lastIndex = 0;
  return DARK_MEDIA.test(html);
}

function inject(html: string, css: string): string {
  const tag = `<style data-crm-preview>${css}</style>`;
  if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${tag}</head>`);
  if (/<body[^>]*>/i.test(html)) return html.replace(/<body[^>]*>/i, (m) => `${m}${tag}`);
  return tag + html;
}

function markOutlook(html: string): string {
  if (!/\[data-og[sb]c\]/i.test(html)) return html;
  return /<html[\s>]/i.test(html) ? html.replace(/<html(\s|>)/i, "<html data-ogsc data-ogsb$1") : html.replace(/<body(\s|>)/i, "<body data-ogsc data-ogsb$1");
}

export function darkVersion(html: string): DarkResult {
  if (hasOwnDarkStyles(html)) {
    DARK_MEDIA.lastIndex = 0;
    return {
      html: markOutlook(html.replace(DARK_MEDIA, "@media all")),
      rule: "own_dark_styles",
      explanation: "The email has its own dark styles, so this is how Apple Mail and other apps that honor them show it.",
    };
  }
  return {
    html: markOutlook(
      inject(
        html,
        "html{filter:invert(1) hue-rotate(180deg);background:#fff}img,picture,video,[style*='background-image']{filter:invert(1) hue-rotate(180deg)}",
      ),
    ),
    rule: "inverted",
    explanation: "The email has no dark styles, so this imitates the Gmail and Outlook apps, which flip light backgrounds and dark text on their own. Photos keep their colors.",
  };
}

/**
 * The light version, whatever the viewer's own setting. Without this, a viewer whose computer is in dark mode gets the
 * email's dark rules (white text) inside the "Light" frame, on a white box, so the text vanishes.
 * The email's dark rules are switched off and the page is pinned to the light color scheme.
 */
export function lightVersion(html: string): string {
  DARK_MEDIA.lastIndex = 0;
  return inject(html.replace(DARK_MEDIA, "@media not all"), ":root{color-scheme:light !important}");
}

/** Wraps a fragment in a minimal document and fills merge tokens with sample values for preview. */
export function previewDocument(html: string, sample: Record<string, string> = SAMPLE_MERGE): string {
  const filled = html.replace(/~([A-Za-z]+)\.([A-Za-z0-9_]+)~/g, (m, a: string, b: string) => sample[`${a}.${b}`] ?? m);
  if (/<html[\s>]/i.test(filled)) return filled;
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0">${filled}</body></html>`;
}

export const SAMPLE_MERGE: Record<string, string> = {
  "Contact.FirstName": "Jordan",
  "Contact.LastName": "Rivers",
  "Contact.Nickname": "Jo",
  "Contact.Email": "jordan@example.com",
  "Contact.Company": "Sunset Pines MHP LLC",
};
